interface RoutingNode {
  id: number;
  x: number;
  y: number;
  type: number;
  isGate: boolean;
  isClosed: boolean;
  description: string;
}

interface PathNode {
  node: RoutingNode;
  g: number; // Cost from start
  h: number; // Heuristic to goal
  f: number; // Total cost
  parent: PathNode | null;
}

// Calculate Euclidean distance between two points
function distance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

// Find nearest routing node to a given point
export function findNearestNode(
  x: number,
  y: number,
  nodes: RoutingNode[],
): RoutingNode | null {
  if (nodes.length === 0) return null;

  let nearest = nodes[0];
  let minDist = distance(x, y, nearest.x, nearest.y);

  for (const node of nodes) {
    const dist = distance(x, y, node.x, node.y);
    if (dist < minDist) {
      minDist = dist;
      nearest = node;
    }
  }

  return nearest;
}

// Find neighbors of a node (nodes within connection distance)
function findNeighbors(
  node: RoutingNode,
  allNodes: RoutingNode[],
  maxDistance: number = 300,
): RoutingNode[] {
  const neighbors: RoutingNode[] = [];

  for (const other of allNodes) {
    if (other.id === node.id) continue;
    if (other.isClosed) continue;

    const dist = distance(node.x, node.y, other.x, other.y);
    if (dist <= maxDistance) {
      neighbors.push(other);
    }
  }

  return neighbors;
}

// A* pathfinding algorithm
export function findPath(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  routingNodes: RoutingNode[],
): { x: number; y: number }[] {
  // Find nearest nodes to start and end points
  const startNode = findNearestNode(startX, startY, routingNodes);
  const endNode = findNearestNode(endX, endY, routingNodes);

  if (!startNode || !endNode) {
    console.error("Could not find start or end node");
    return [];
  }

  // If start and end are the same, return direct path
  if (startNode.id === endNode.id) {
    return [
      { x: startX, y: startY },
      { x: endX, y: endY },
    ];
  }

  const openSet: PathNode[] = [];
  const closedSet = new Set<number>();

  // Initialize start node
  const startPathNode: PathNode = {
    node: startNode,
    g: 0,
    h: distance(startNode.x, startNode.y, endNode.x, endNode.y),
    f: 0,
    parent: null,
  };
  startPathNode.f = startPathNode.g + startPathNode.h;
  openSet.push(startPathNode);

  while (openSet.length > 0) {
    // Find node with lowest f score
    openSet.sort((a, b) => a.f - b.f);
    const current = openSet.shift()!;

    // Check if we reached the goal
    if (current.node.id === endNode.id) {
      // Reconstruct path
      const path: { x: number; y: number }[] = [];
      let pathNode: PathNode | null = current;

      while (pathNode) {
        path.unshift({ x: pathNode.node.x, y: pathNode.node.y });
        pathNode = pathNode.parent;
      }

      // Add actual start and end points
      path.unshift({ x: startX, y: startY });
      path.push({ x: endX, y: endY });

      return path;
    }

    closedSet.add(current.node.id);

    // Check neighbors
    const neighbors = findNeighbors(current.node, routingNodes);

    for (const neighbor of neighbors) {
      if (closedSet.has(neighbor.id)) continue;

      const g =
        current.g +
        distance(current.node.x, current.node.y, neighbor.x, neighbor.y);
      const h = distance(neighbor.x, neighbor.y, endNode.x, endNode.y);
      const f = g + h;

      // Check if neighbor is already in open set
      const existingNode = openSet.find((n) => n.node.id === neighbor.id);

      if (existingNode) {
        // Update if we found a better path
        if (g < existingNode.g) {
          existingNode.g = g;
          existingNode.f = f;
          existingNode.parent = current;
        }
      } else {
        // Add to open set
        openSet.push({
          node: neighbor,
          g,
          h,
          f,
          parent: current,
        });
      }
    }
  }

  // No path found, return direct line
  console.warn("No path found, returning direct line");
  return [
    { x: startX, y: startY },
    { x: endX, y: endY },
  ];
}

// Calculate total path distance
export function calculatePathDistance(
  path: { x: number; y: number }[],
): number {
  let totalDistance = 0;

  for (let i = 0; i < path.length - 1; i++) {
    totalDistance += distance(
      path[i].x,
      path[i].y,
      path[i + 1].x,
      path[i + 1].y,
    );
  }

  return Math.round(totalDistance);
}

